// Stage 33: Bulletin Canvas — frontend API client.
// Stage 29 refactor: tasks come from yesterday_tasks; createTask /
// updateTask / deleteTask / runTaskNow / listTasks helpers removed
// (those flows live in the Yesterday tab now).

import { apiGet, apiJson } from '../api/client';
import type {
  BulletinPanelDTO,
  PanelHistoryEntry,
  PanelRenderResponse,
  UnlinkedYesterdayTaskDTO,
} from './types';

export async function listPanels(): Promise<BulletinPanelDTO[]> {
  const r = await apiGet<{ panels: BulletinPanelDTO[] }>(
    '/api/bulletin/panels',
  );
  return r.panels;
}

export async function createPanel(
  x: number,
  y: number,
): Promise<BulletinPanelDTO> {
  const r = await apiJson<{ panel: BulletinPanelDTO }>(
    'POST',
    '/api/bulletin/panels',
    { x, y },
  );
  return r.panel;
}

export async function renamePanel(
  id: string,
  displayLabel: string,
): Promise<void> {
  await apiJson<{ ok: true }>(
    'PATCH',
    `/api/bulletin/panels/${encodeURIComponent(id)}`,
    { displayLabel },
  );
}

export async function updateGeometry(
  id: string,
  geom: Partial<{
    x: number;
    y: number;
    w: number;
    h: number;
    zIndex: number;
  }>,
): Promise<void> {
  await apiJson<{ ok: true }>(
    'PATCH',
    `/api/bulletin/panels/${encodeURIComponent(id)}/geometry`,
    geom,
  );
}

export async function bindPanel(
  id: string,
  linkedYesterdayId: string | null,
): Promise<void> {
  await apiJson<{ ok: true }>(
    'PATCH',
    `/api/bulletin/panels/${encodeURIComponent(id)}/binding`,
    { linkedYesterdayId },
  );
}

export async function deletePanel(id: string): Promise<void> {
  await apiJson<{ ok: true }>(
    'DELETE',
    `/api/bulletin/panels/${encodeURIComponent(id)}`,
  );
}

export async function getPanelRender(
  id: string,
  versionKey?: string,
): Promise<PanelRenderResponse> {
  const qs = versionKey
    ? `?version=${encodeURIComponent(versionKey)}`
    : '';
  return apiGet<PanelRenderResponse>(
    `/api/bulletin/panels/${encodeURIComponent(id)}/render${qs}`,
  );
}

export async function getPanelHistory(
  id: string,
): Promise<PanelHistoryEntry[]> {
  const r = await apiGet<{ versions: PanelHistoryEntry[] }>(
    `/api/bulletin/panels/${encodeURIComponent(id)}/history`,
  );
  return r.versions;
}

export async function listUnlinkedTasks(): Promise<UnlinkedYesterdayTaskDTO[]> {
  const r = await apiGet<{ tasks: UnlinkedYesterdayTaskDTO[] }>(
    '/api/bulletin/tasks/unlinked',
  );
  return r.tasks;
}

/**
 * Stage 33: pin (or unpin with `null`) a specific R2 version as the
 * panel's rendered surface. The pin survives subsequent task runs
 * until cleared. Server validates that non-null keys belong to the
 * panel's bound task prefix.
 */
export async function pinVersion(
  panelId: string,
  versionKey: string | null,
): Promise<void> {
  await apiJson<{ ok: true; pinnedR2Key: string | null }>(
    'PATCH',
    `/api/bulletin/panels/${encodeURIComponent(panelId)}/pin-version`,
    { versionKey },
  );
}

/**
 * Stage 39: toggle the panel's collapsed flag. Collapsed panels render
 * title-only on the canvas AND are excluded from the daily briefing
 * yesterday-task summarisation.
 */
export async function setCollapsed(
  panelId: string,
  collapsed: boolean,
): Promise<void> {
  await apiJson<{ ok: true; collapsed: boolean }>(
    'PATCH',
    `/api/bulletin/panels/${encodeURIComponent(panelId)}/collapsed`,
    { collapsed },
  );
}
